/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import './Login.css';
import AdobeClearSpace from '@exc/brand-icons/AdobeClearSpace';
import AdobeExperienceCloud from '@exc/brand-icons/ExperienceCloudColor';
import {AuthInstance} from '@exc/auth';
import Button from '@react/react-spectrum/Button';
import {FormattedMessage, withLocalization} from '@adobe/adobe-intl-react';
import Icon from '@react/react-spectrum/Icon';
import Metrics from '@exc/metrics';
import React, {useEffect} from 'react';

const year = new Date().getFullYear();
const metrics = new Metrics('exc.core.modules.Login');

function Login({formatMessage, query = {}}) {
  useEffect(() => {
    document.title = 'Login | Adobe Experience Cloud';
    metrics.event('Login.imsSSO.ready');
  }, []);

  function handleSignInWithId() {
    let imsRedirectUri = window.location.origin;
    if (query.redirectlocation) {
      imsRedirectUri = query.redirectlocation;
    }
    AuthInstance().then(auth => auth.signIn(imsRedirectUri));
  }

  return (
    <main>
      <div className="login-background" />
      <Icon className="login-adobe-logo"><AdobeClearSpace /></Icon>
      <div className="login-box">
        <div className="login-leftbox">
          <Icon className="login-expcloud-icon" size="L"><AdobeExperienceCloud /></Icon>
          <h1 className="login-title">Adobe Experience Cloud</h1>
          <div className="login-sign-in">
            <Button
              className="login-sign-in-button"
              label={formatMessage({
                defaultMessage: 'Sign In with an Adobe ID',
                description: 'Label for button to sign in with Adobe ID',
                messageId: 'signInWithId'
              })}
              onClick={handleSignInWithId}
              variant="cta"
            />
          </div>
        </div>
      </div>
      <ul className="login-usage-box">
        <li className="login-left-li">
          <a className="login-link" href="http://www.adobe.com/go/marketingcloud_privacy">
            <FormattedMessage
              defaultMessage="Privacy (UPDATED)"
              description="Label for link to privacy information"
              messageId="privacy"
            />
          </a>
        </li>
        <li className="login-left-li">
          <a className="login-link" href="https://marketing.adobe.com/resources/help/en_US/terms.html">
            <FormattedMessage
              defaultMessage="Terms of Use"
              description="Label for link to Terms of Use"
              messageId="termsOfUse"
            />
          </a>
        </li>
        <li className="login-left-li">
          <a className="login-link" href="http://www.adobe.com/in/privacy/cookies.html">
            <FormattedMessage
              defaultMessage="Cookies"
              description="Label for link to cookies information"
              messageId="cookies"
            />
          </a>
        </li>
        <li className="login-left-li">
          <a className="login-link" href="https://helpx.adobe.com/marketing-cloud.html">
            <FormattedMessage
              defaultMessage="Help"
              description="Label for link to help"
              messageId="help"
            />
          </a>
        </li>
        <li className="login-right-li">{`© ${year} Adobe Systems Incorporated. All Rights Reserved.`}</li>
      </ul>
    </main>
  );
}

export default withLocalization(Login);
